import { useState, useEffect, useCallback } from 'react'
import { getGoogleSheetData } from '../utils/googleSheets'
import { Booking, SimplifiedBooking } from '../types/booking'

interface UseGoogleSheetsOptions {
  sheetUrl: string
  refreshInterval?: number // Auto-refresh interval in milliseconds (optional)
  enabled?: boolean // Whether to fetch data (default: true)
}

interface UseGoogleSheetsResult {
  data: Booking[]
  loading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

// Equipment name constant (since there's only one piece of equipment)
const EQUIPMENT_NAME = 'Andros Ultra Binder'
const EQUIPMENT_ID = 'andros-01'

/**
 * Converts simplified booking data to full booking format
 */
function convertToFullBooking(simplified: SimplifiedBooking): Booking {
  return {
    ...simplified,
    machineId: EQUIPMENT_ID,
    machineName: EQUIPMENT_NAME,
  }
}

/**
 * Custom hook to fetch and manage Google Sheets data
 * Automatically converts simplified booking format to full format
 *
 * @example
 * const { data, loading, error, refetch } = useGoogleSheets({
 *   sheetUrl: 'https://docs.google.com/spreadsheets/d/e/YOUR_SHEET_ID/pub?output=csv',
 *   refreshInterval: 30000, // Refresh every 30 seconds (optional)
 * })
 */
export function useGoogleSheets({
  sheetUrl,
  refreshInterval,
  enabled = true,
}: UseGoogleSheetsOptions): UseGoogleSheetsResult {
  const [data, setData] = useState<Booking[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchData = useCallback(async () => {
    if (!enabled || !sheetUrl) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Fetch simplified data from Google Sheets
      const simplifiedData =
        await getGoogleSheetData<SimplifiedBooking>(sheetUrl)

      // Convert to full booking format
      const fullBookings = simplifiedData.map(convertToFullBooking)

      setData(fullBookings)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch data'))
      console.error('Error fetching Google Sheets data:', err)
    } finally {
      setLoading(false)
    }
  }, [sheetUrl, enabled])

  // Initial fetch
  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Auto-refresh if interval is set
  useEffect(() => {
    if (!refreshInterval || !enabled) return

    const intervalId = setInterval(fetchData, refreshInterval)
    return () => clearInterval(intervalId)
  }, [refreshInterval, enabled, fetchData])

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  }
}
